import '../modal/ui-modal-controller';
import { UIElement } from '../ui-element';
import { Children } from '../../decorators/child.decorator';
import { UIToggleTab } from './ui-toggle-tab';
import { Component } from '../../decorators/component.decorator';
import styles from './ui-toggle-tabbar.css';
import { Labels } from '../../global/labels';
import { Digest } from '../../global/digest';
import { Attribute } from '../../decorators/attribute.decorator';

export const UIToggleTabbarLayout = {
    Default: 'default',
    Fdp: 'fdp',
} as const;

export type UIToggleTabbarLayoutValue =
    (typeof UIToggleTabbarLayout)[keyof typeof UIToggleTabbarLayout];

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIToggleTabbar
 * @element ui-toggle-tabbar
 * @classdesc Represents a class for <code>ui-toggle-tabbar</code> element.
 * It is meant to be used as a wrapper for a group of <code>ui-toggle-tab</code> elements.
 * @property {UIToggleTabbarLayoutValue} [layout="default"] {@attr layout} Layout of the toggle tabbar.
 * {@desc default} Default layout - used in the older style header.
 * {@desc fdp} FDP layout - used in the newer FDP header.
 * @property {UIToggleTab[]} toggleTabs - Shortcut to toggle tab elements.
 * @example
 * <ui-toggle-tabbar>
 *   <ui-toggle-tab label="Private" name="userProfile" value="private" checked></ui-toggle-tab>
 *   <ui-toggle-tab label="Business" name="userProfile" value="business"></ui-toggle-tab>
 * </ui-toggle-tabbar>
 */
@Component({
    tag: 'ui-toggle-tabbar',
    styles,
})
export class UIToggleTabbar extends UIElement {
    @Attribute(String) layout: UIToggleTabbarLayoutValue =
        UIToggleTabbarLayout.Default;

    @Children('ui-toggle-tab')
    toggleTabs!: NodeListOf<UIToggleTab>;

    private _descriptionId: string = '';

    private changeHandler: EventListener = (event: Event): void => {
        this.handleChange(event);
    };

    static get labels() {
        return Labels.attach('ui-toggle-tabbar', {
            navigationHint:
                'Selecting an option will navigate to a different page',
        });
    }

    /**
     * @private
     */
    private get descriptionId(): string {
        if (!this._descriptionId) {
            this._descriptionId = Digest.randomId();
        }
        return this._descriptionId;
    }

    /**
     * Ensures at least one switch item is checked, defaulting to the first item.
     * @private
     */
    private ensureCheckedItem(): void {
        const checkedItem = this.querySelector('ui-toggle-tab[checked]');

        if (!checkedItem) {
            this.toggleTabs[0]?.setAttribute('checked', '');
        }
    }

    /**
     * Cleanup checked state of other switch items when one of them is checked.
     * @param {Event} event
     * @private
     */
    private handleChange(event: Event): void {
        const target = event.target as HTMLInputElement;

        this.querySelectorAll('ui-toggle-tab[checked]').forEach(
            (item: Element): void => {
                const toggleTab = item as UIToggleTab;

                if (toggleTab.inputElement !== target) {
                    toggleTab.removeAttribute('checked');
                }
            }
        );
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.setAttribute('role', 'radiogroup');

        if (!this.hasAttribute('layout')) {
            this.setAttribute('layout', UIToggleTabbarLayout.Default);
        }

        this.append(
            this.createElement({
                tagName: 'span',
                classList: { '-visually-hidden': true },
                attributes: { id: this.descriptionId },
                children: [UIToggleTabbar.labels.navigationHint],
            })
        );
        this.setAttribute('aria-describedby', this.descriptionId);
        this.ensureCheckedItem();
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.addEventListener('change', this.changeHandler);
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.removeEventListener('change', this.changeHandler);
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.addEventListener('change', this.changeHandler);
    }
}
